/**
 * Shared catalog of every BuildPilot workspace tool.
 *
 * Single source of truth so:
 *   - ToolboxPage renders the iOS-style grid
 *   - contentIndex.js feeds the global search bar suggestions
 *
 * Each entry mirrors what iOS shows in the Toolbox tab. Order matches
 * the iOS screenshot row-by-row.
 *
 * Fields:
 *   id        - stable kebab-case id
 *   name      - display title
 *   icon      - filename under /public/workspace-icons/toolbox/
 *   route     - destination inside /workspace/*
 *   premium?  - true if locked behind the Premium subscription
 *   keywords? - extra search terms (synonyms, what the tool does)
 */
export const TOOL_ICON_BASE = '/workspace-icons/toolbox';

export const WORKSPACE_TOOLS = [
  {
    id: 'cost-tracker', name: 'Cost Tracker',
    icon: 'cost tracker.png', route: '/workspace/tools/cost-tracker',
    keywords: 'budget expenses spending track money invoices receipts costs',
  },
  {
    id: 'milestones', name: 'Milestones',
    icon: 'milestones.png', route: '/workspace/tools/milestones',
    keywords: 'progress stages build schedule timeline construction phases',
  },
  {
    id: 'checklists', name: 'Checklists',
    icon: 'checklists.png', route: '/workspace/tools/checklists',
    keywords: 'todo tasks list inspections handover defects pre-purchase',
  },
  {
    id: 'contacts-hub', name: 'Contacts Hub',
    icon: 'contacts-hub.png', route: '/workspace/tools/contacts',
    keywords: 'builder tradies suppliers phone numbers email directory people',
  },
  {
    id: 'house-and-land', name: 'House & Land',
    icon: 'house-and-land.png', route: '/workspace/tools/house-and-land',
    keywords: 'block land lot package matchmaker estate suburb find',
  },
  {
    id: 'documents', name: 'Documents',
    icon: 'documents.png', route: '/workspace/tools/documents',
    keywords: 'contracts pdf files upload storage plans drawings',
  },
  {
    id: 'government-resources', name: 'Government Resources',
    icon: 'government resources.png', route: '/workspace/tools/government-resources',
    keywords: 'fhog stamp duty grants rebate council planning approval',
  },
  {
    id: 'builder-licence-check', name: 'Builder Licence Check',
    icon: 'builder license check.png', route: '/workspace/tools/builder-licence-check',
    keywords: 'license verification cbs cbs check registered approved',
  },
  {
    id: 'building-insurance-check', name: 'Building Insurance Check',
    icon: 'building insurance check.png', route: '/workspace/tools/building-insurance-check',
    keywords: 'biw home warranty insurance cover claim builder default',
  },
  {
    id: 'solar-orientation', name: 'Solar Orientation Guide',
    icon: 'solar Orientation guide.png', route: '/workspace/tools/solar-orientation',
    keywords: 'sun north facing passive design energy efficient orientation',
  },
  {
    id: 'project-health', name: 'Project Health',
    icon: 'project-health-brain.png', route: '/workspace/tools/intelligence',
    premium: true,
    keywords: 'ai brain analysis risk score insights summary intelligence health',
  },
  {
    id: 'quote-comparison', name: 'Quote Comparison',
    icon: 'quote-comparison-icon.png', route: '/workspace/tools/quote-comparison',
    premium: true,
    keywords: 'compare tenders quotes builders side by side ai analysis',
  },
  {
    id: 'find-a-builder', name: 'Find a Builder',
    icon: 'find a business.png', route: '/workspace/tools/find-a-builder',
    keywords: 'builders matchmaker shortlist adelaide compare',
  },
  {
    id: 'find-a-tradie', name: 'Find a Tradie',
    icon: 'find-a-tradie.png', route: '/workspace/tools/find-a-tradie',
    keywords: 'tradesperson electrician plumber carpenter tiler painter',
  },
  {
    id: 'find-a-supplier', name: 'Find a Supplier',
    icon: 'find a business.png', route: '/workspace/tools/find-a-supplier',
    keywords: 'kitchen tiles flooring lighting brick supplier vendor',
  },
  {
    id: 'facade-visualiser', name: 'Facade Visualiser',
    icon: 'facade-visualiser.png', route: '/workspace/tools/facade-visualiser',
    keywords: 'ai render house exterior elevation style mood image generate',
  },
  {
    id: 'defects-scanner', name: 'Defects Scanner',
    icon: 'defects scanner.png', route: '/workspace/tools/defects-scanner',
    premium: true,
    keywords: 'ai photo handover defects snags issues report inspection',
  },
  {
    id: 'furniture-planner', name: 'Furniture Planner',
    icon: 'furniture-planner.png', route: '/workspace/tools/furniture-planner',
    keywords: 'room layout furniture floorplan interior design arrange',
  },
  {
    id: 'favourite-homes', name: 'Favourite Homes',
    icon: 'house-and-land.png', route: '/workspace/tools/favourite-homes',
    keywords: 'favourites inspiration save bookmark homes display centre',
  },
];

export const toolIconUrl = (tool) =>
  `${TOOL_ICON_BASE}/${encodeURIComponent(tool.icon)}`;
